import {
  File,
  FileText,
  Image,
  Presentation,
  Sheet,
  Video,
  X,
} from "lucide-react";
import type React from "react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

interface FileAttachmentProps {
  files: File[];
  fileError: string | null;
  onRemoveFile: (index: number) => void;
}

export const FileAttachment: React.FC<FileAttachmentProps> = ({
  files,
  fileError,
  onRemoveFile,
}) => {
  const { t } = useTranslation();

  // ファイルサイズを適切な単位に変換
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
  };

  // ファイル名を指定文字数で切り取り
  const truncateFileName = (fileName: string, maxLength = 10): string => {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split(".").pop() || "";
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf("."));
    const truncatedName = nameWithoutExt.substring(0, maxLength - 3);
    return `${truncatedName}...${extension ? `.${extension}` : ""}`;
  };

  // ファイル拡張子に基づいてアイコンを取得
  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase() || "";

    // 画像ファイル
    if (
      ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(extension)
    ) {
      return <Image size={24} className="text-blue-500" />;
    }

    // 動画ファイル
    if (
      ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"].includes(extension)
    ) {
      return <Video size={24} className="text-purple-500" />;
    }

    // PDFファイル
    if (extension === "pdf") {
      return <FileText size={24} className="text-red-500" />;
    }

    // Excelファイル
    if (["xlsx", "xls", "csv"].includes(extension)) {
      return <Sheet size={24} className="text-green-500" />;
    }

    // PowerPointファイル
    if (["pptx", "ppt"].includes(extension)) {
      return <Presentation size={24} className="text-orange-500" />;
    }

    // Wordファイル
    if (["docx", "doc"].includes(extension)) {
      return <FileText size={24} className="text-blue-600" />;
    }

    // テキストファイル
    if (["txt", "md", "rtf"].includes(extension)) {
      return <FileText size={24} className="text-gray-600" />;
    }

    // その他
    return <File size={24} className="text-gray-500" />;
  };

  const filePreviews = useMemo(
    () =>
      files.map((file) => ({
        file,
        previewUrl: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
      })),
    [files],
  );

  useEffect(() => {
    return () => {
      filePreviews.forEach(({ previewUrl }) => {
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
      });
    };
  }, [filePreviews]);

  const renderFileCard = (
    file: File,
    index: number,
    previewUrl: string | null,
  ) => {
    return (
      <div
        key={`${file.name}-${index}`}
        className="relative h-20 w-[180px] rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md"
      >
        <div className="flex h-full items-start gap-3">
          {/* ファイルアイコン */}
          <div className="mt-1 shrink-0">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={file.name}
                className="size-12 rounded object-cover"
                loading="lazy"
              />
            ) : (
              getFileIcon(file.name)
            )}
          </div>

          {/* ファイル情報 */}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-gray-900">
              {truncateFileName(file.name)}
            </div>
            <div className="mt-1 text-xs text-gray-500">
              {formatFileSize(file.size)}
            </div>
          </div>
        </div>

        {/* 削除ボタン */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute -right-2 -top-2 size-6 rounded-full border border-gray-300 bg-white shadow-sm transition-all duration-200 hover:scale-110 hover:border-red-300 hover:bg-red-50"
          onClick={() => onRemoveFile(index)}
          aria-label="Remove file"
        >
          <X size={12} className="text-gray-600 hover:text-red-500" />
        </Button>
      </div>
    );
  };

  return (
    <>
      {/* File Error Display */}
      {fileError && (
        <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-500">
          {fileError}
        </div>
      )}

      {/* Attached Files Preview */}
      {files.length > 0 && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="mb-3 text-sm font-medium text-gray-700">
            {files.length} {t("common.filesAttachedCount")}
          </div>
          <div className="flex flex-wrap gap-3">
            {filePreviews.map(({ file, previewUrl }, index) =>
              renderFileCard(file, index, previewUrl),
            )}
          </div>
        </div>
      )}
    </>
  );
};
